import React, { useState, useEffect, useCallback } from 'react';
import { Icon, Modal } from '../components/ui';
import { useNavigate } from 'react-router-dom';
import { copyToClipboard } from '../lib/utils';
import { useTranslation } from 'react-i18next';
import { formatRelative } from '../i18n/format';
import { parseServerDate } from '../i18n/format';
import AgentControlPanel from '../components/AgentControlPanel';

interface EndpointAgentsProps {
  apiUrl: string;
}

interface BdrDevice {
  device_id: string;
  org_id: string;
  hostname: string | null;
  os: string | null;
  ips: string | null; // JSON-encoded array
  kind: 'agent' | 'browser' | 'edge' | 'mobile';
  /** Mobile only: the person the phone was enrolled for (from MDM managed config). */
  owner_user?: string | null;
  status: 'active' | 'revoked';
  enforcement?: 'inherit' | 'block' | 'allow';
  enrolled_at: string;
  last_seen: string | null;
  // Supervisor heartbeat (POST /v1/bdr/heartbeat). Absent on older servers.
  agent_version?: string | null;
  health_state?: 'ok' | 'degraded' | null;
  health_at?: string | null;
  health_detail?: AgentHealthDetail | null;
  /** Server-derived: 'no_heartbeat' when none or older than 3 minutes. */
  health?: 'ok' | 'degraded' | 'no_heartbeat';
}

interface AgentComponentHealth {
  running?: boolean | number;
  pid?: number;
  restarts_1h?: number;
  restarts_total?: number;
  last_exit?: string | null;
  last_restart_reason?: string | null;
  up_secs?: number;
  port?: number;
  coverage?: string;
  sessions?: number;
}

interface AgentHealthDetail {
  version?: string;
  state?: 'ok' | 'degraded';
  issues?: { key: string; detail: string }[];
  components?: Record<string, AgentComponentHealth | undefined>;
  enrolled?: boolean;
  capture_available?: boolean;
  policy_age_secs?: number;
  uptime_secs?: number;
}

// Agent self-defense alert — the agent reports possible tampering (killed,
// uninstalled, scheduled task removed). Raw audit_logs row shape.
interface TamperEvent {
  id: number;
  timestamp: string;
  tool_name: string; // tamper-type label, e.g. "scheduled_task_removed"
  result: string; // human-readable detail
  agent_id: string; // device id
}

const LAYER_LABEL: Record<string, string> = { agent: 'Network agent', browser: 'Browser ext', edge: 'Loopback proxy', mobile: 'Mobile' };
const LAYER_BADGE: Record<string, string> = { agent: 'badge-info', browser: 'badge-redact', edge: 'badge-success', mobile: 'badge-warning' };

const HEALTH_BADGE: Record<string, string> = { ok: 'badge-success', degraded: 'badge-warning', no_heartbeat: 'badge-unknown' };
const HEALTH_LABEL_KEY: Record<string, string> = {
  ok: 'pages.endpointAgents.healthHealthy',
  degraded: 'pages.endpointAgents.healthDegraded',
  no_heartbeat: 'pages.endpointAgents.healthNoHeartbeat',
};

// Server timestamps are SQLite-style 'YYYY-MM-DD HH:MM:SS' in UTC with no zone marker.
function parseServerTime(v: string | null | undefined): number | null {
  if (!v) return null;
  const s = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(v) ? v.replace(' ', 'T') + 'Z' : v;
  const t = Date.parse(s);
  return Number.isNaN(t) ? null : t;
}

function parseIps(ips: string | null): string[] {
  if (!ips) return [];
  try {
    const arr = JSON.parse(ips);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

const getOS_TARGETS = (t: (key: string) => string): { key: string; label: string; icon: string }[] => [
  { key: 'windows', label: t('pages.endpointAgents.windows'), icon: 'download' },
  { key: 'macos', label: 'macOS', icon: 'download' },
  { key: 'linux', label: t('pages.endpointAgents.linux'), icon: 'download' },
];

// Expanded per-device heartbeat detail: issues, per-component supervisor state,
// capture availability and heartbeat age. Reuses the audit-log detail-row styles.
const AgentHealthPanel: React.FC<{ detail: AgentHealthDetail | null; heartbeatAgo: string; health: string }> = ({
  detail,
  heartbeatAgo,
  health,
}) => {
  const { t: tr } = useTranslation();
  const issues = detail?.issues || [];
  const components = Object.entries(detail?.components || {}).filter(
    (e): e is [string, AgentComponentHealth] => !!e[1],
  );
  const capture =
    detail?.capture_available === true
      ? tr('pages.endpointAgents.captureAvailable')
      : detail?.capture_available === false
      ? tr('pages.endpointAgents.captureUnavailable')
      : tr('pages.endpointAgents.unknown');
  const describe = (c: AgentComponentHealth): string => {
    const parts: string[] = [];
    if (typeof c.running === 'number' && typeof c.sessions === 'number') {
      parts.push(tr('pages.endpointAgents.traySessions', { running: c.running, sessions: c.sessions }));
    } else if (typeof c.running === 'boolean') {
      parts.push(c.running ? tr('pages.endpointAgents.running') : tr('pages.endpointAgents.notRunning'));
    }
    if (typeof c.restarts_1h === 'number') parts.push(tr('pages.endpointAgents.restarts1h', { count: c.restarts_1h }));
    if (c.last_restart_reason) parts.push(tr('pages.endpointAgents.lastRestartReason', { reason: c.last_restart_reason }));
    return parts.join(' · ') || '—';
  };
  const failing = (c: AgentComponentHealth) => c.running === false || (c.restarts_1h ?? 0) > 0;
  return (
    <div className="audit-detail">
      <div className="audit-detail-grid">
        <div><strong>{tr('pages.endpointAgents.heartbeat')}:</strong> {heartbeatAgo}</div>
        <div><strong>{tr('pages.endpointAgents.capture')}:</strong> {capture}</div>
        <div><strong>{tr('pages.endpointAgents.agent')}:</strong> {detail?.version || '—'}</div>
      </div>
      {health === 'no_heartbeat' && (
        <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 10 }}>{tr('pages.endpointAgents.healthNoHeartbeatHint')}</div>
      )}
      {components.length > 0 && (
        <div className="audit-detail-result" style={{ marginBottom: 10 }}>
          <strong>{tr('pages.endpointAgents.components')}</strong>
          <ul style={{ margin: '6px 0 0', paddingInlineStart: 18, lineHeight: 1.7 }}>
            {components.map(([name, c]) => (
              <li key={name} style={failing(c) ? { color: 'var(--warning)' } : undefined}>
                <code>{name}</code> — {describe(c)}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="audit-detail-result">
        <strong>{tr('pages.endpointAgents.issues')}</strong>
        {issues.length ? (
          <ul style={{ margin: '6px 0 0', paddingInlineStart: 18, lineHeight: 1.7 }}>
            {issues.map((i, idx) => (
              <li key={`${i.key}-${idx}`}>{i.detail || i.key}</li>
            ))}
          </ul>
        ) : (
          <div style={{ marginTop: 6 }}>{detail ? tr('pages.endpointAgents.noIssuesReported') : '—'}</div>
        )}
      </div>
    </div>
  );
};

const EndpointAgents: React.FC<EndpointAgentsProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const { t: tr, i18n } = useTranslation();
  const [devices, setDevices] = useState<BdrDevice[]>([]);
  const [count, setCount] = useState(0);
  const [tamperEvents, setTamperEvents] = useState<TamperEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const [showEnroll, setShowEnroll] = useState(false);
  const [enrollToken, setEnrollToken] = useState<string | null>(null);
  const [enrollExpiry, setEnrollExpiry] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  // LLM target entitlements live on their own page (Access Control -> LLM
  // Access Policy). This page only links across to it, pre-scoped to the row.
  const navigate = useNavigate();

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetch(`${apiUrl}/admin/bdr/devices`).then((r) => r.json());
      setDevices(d.devices || []);
      setCount(d.count || 0);
      const t = await fetch(`${apiUrl}/admin/bdr/tamper-events`).then((r) => r.json());
      setTamperEvents(t.events || []);
    } catch (err: any) {
      setError(`Cannot reach endpoint-agent API: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const generateToken = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${apiUrl}/admin/bdr/enroll-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expires_hours: 24 }),
      }).then((r) => r.json());
      setEnrollToken(res.enrollment_token);
      setEnrollExpiry(res.expires_at);
    } catch (err: any) {
      flash(`Token generation failed: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const openEnroll = () => {
    setEnrollToken(null);
    setEnrollExpiry(null);
    setShowEnroll(true);
    generateToken();
  };

  const revoke = async (deviceId: string) => {
    setBusy(deviceId);
    try {
      await fetch(`${apiUrl}/admin/bdr/devices/${deviceId}/revoke`, { method: 'POST' });
      flash('Device revoked.');
      await loadAll();
    } catch (err: any) {
      flash(`Revoke failed: ${err.message}`);
    } finally {
      setBusy(null);
    }
  };

  // On-demand per-endpoint block/unblock. 'block' quarantines just this endpoint
  // from all AI; 'allow' exempts it; the agent applies/reverts on its next poll.
  const setEnforcement = async (deviceId: string, mode: 'block' | 'allow') => {
    if (mode === 'block' && !window.confirm(tr('pages.endpointAgents.blockAllAIConfirm'))) return;
    setBusy(deviceId);
    try {
      await fetch(`${apiUrl}/admin/bdr/devices/${deviceId}/enforcement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      });
      flash(mode === 'block'
        ? 'Endpoint blocked — quarantined from all AI on its next check-in (~20s).'
        : 'Endpoint unblocked — agent clears its block rules on the next check-in.');
      await loadAll();
    } catch (err: any) {
      flash(`Enforcement update failed: ${err.message}`);
    } finally {
      setBusy(null);
    }
  };

  // Auth-aware download: fetch the artifact (carrying interceptor headers) then save the blob.
  // Windows and macOS ship a zip (installer + per-customer config baked with this server URL).
  const download = async (os: string, label: string) => {
    setBusy(`dl-${os}`);
    try {
      const zipped = os === 'windows' || os === 'macos';
      const qs = zipped ? `?server=${encodeURIComponent(deriveExtensionServer())}` : '';
      const resp = await fetch(`${apiUrl}/admin/bdr/download/${os}${qs}`);
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = zipped ? `ztai-bdr-agent-${os}.zip` : `ztai-bdr-agent-${os}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      flash(`Downloading ${label} agent…`);
    } catch (err: any) {
      flash(`${label} download unavailable: ${err.message}`);
    } finally {
      setBusy(null);
    }
  };

  // The extension's Agenti5 server defaults to the customer's API host, derived from
  // the dashboard hostname by inserting an `-api` label and forcing https:
  //   dashboard  acme.poc.fidar.ai   →   https://acme-api.poc.fidar.ai
  // (This is distinct from `apiUrl`, which is the dashboard's own :3000 API.)
  const deriveExtensionServer = (): string => {
    try {
      const host = window.location.hostname; // e.g. acme.poc.fidar.ai
      const isIp = /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
      if (!host || host === 'localhost' || isIp || !host.includes('.')) {
        return apiUrl; // dev/local: fall back to the dashboard's API url
      }
      const dot = host.indexOf('.');
      const first = host.slice(0, dot); // acme
      const rest = host.slice(dot + 1); // poc.fidar.ai
      const label = first.endsWith('-api') ? first : `${first}-api`;
      return `https://${label}.${rest}`; // https://acme-api.poc.fidar.ai
    } catch {
      return apiUrl;
    }
  };

  // Download the managed browser extension, pre-packaged with this server's URL
  // and a fresh enrollment token baked in (server derives both).
  const downloadExtension = async () => {
    setBusy('dl-ext');
    try {
      const server = deriveExtensionServer();
      const resp = await fetch(`${apiUrl}/admin/bdr/download/extension?server=${encodeURIComponent(server)}`);
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ztai-shadow-ai-guard.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      flash('Downloading browser extension…');
    } catch (err: any) {
      flash(`Extension download unavailable: ${err.message}`);
    } finally {
      setBusy(null);
    }
  };

  const copy = async (text: string) => {
    const ok = await copyToClipboard(text);
    flash(ok ? 'Copied to clipboard.' : 'Copy failed — select and copy manually.');
  };

  const recentTamperEvents = tamperEvents.filter(
    (t) => Date.now() - new Date(t.timestamp).getTime() < 24 * 60 * 60 * 1000
  );

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>
            <Icon name="chip" size={20} /> {tr('pages.endpointAgents.endpointAgents')}
          </h1>
          <p className="subtitle">
            {tr('pages.endpointAgents.lightweightBDRAgentsOnCorporateLaptops')}
          </p>
        </div>
        <div className="ent-toolbar" style={{ margin: 0 }}>
          <button className="ent-btn primary" onClick={openEnroll}>
            <Icon name="key" /> {tr('pages.endpointAgents.enrollDevice')}
          </button>
          <button className="ent-btn" onClick={loadAll} disabled={loading}>
            <Icon name="refresh" className={loading ? 'spin' : ''} /> {tr('pages.endpointAgents.refresh')}
          </button>
        </div>
      </div>

      {error && <div className="ent-empty">{error}</div>}

      {recentTamperEvents.length > 0 && (
        <div className="notice error" style={{ marginBottom: 16 }}>
          <Icon name="warning" />
          <div>
            <strong>
              {tr('pages.endpointAgents.tamperEventsLast24h', { count: recentTamperEvents.length })}
            </strong>
            <div style={{ fontSize: 12, marginTop: 2 }}>
              {recentTamperEvents.slice(0, 3).map((t) => `${t.agent_id}: ${t.tool_name}`).join(' · ')}
              {recentTamperEvents.length > 3 ? ` · +${recentTamperEvents.length - 3} more` : ''}
            </div>
          </div>
        </div>
      )}

      {/* Counters — deliberately distinct from AI Agents */}
      <div className="metric-row">
        <div className="metric accent-info">
          <span className="label">
            <Icon name="chip" /> {tr('pages.endpointAgents.endpointAgents')}
          </span>
          <div className="value">{count}</div>
        </div>
        <div className="metric accent-success">
          <span className="label">
            <Icon name="checkBadge" /> {tr('pages.endpointAgents.active')}
          </span>
          <div className="value">{devices.filter((d) => d.status === 'active').length}</div>
        </div>
        <div className="metric accent-warning">
          <span className="label">
            <Icon name="deny" /> {tr('pages.endpointAgents.revoked')}
          </span>
          <div className="value">{devices.filter((d) => d.status === 'revoked').length}</div>
        </div>
        <div className="metric accent-danger">
          <span className="label">
            <Icon name="shield" /> {tr('pages.endpointAgents.blocked')}
          </span>
          <div className="value">{devices.filter((d) => d.enforcement === 'block').length}</div>
        </div>
      </div>

      {/* Download panel */}
      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">
          <span><Icon name="download" /> {tr('pages.endpointAgents.downloadEndpointAgent')}</span>
          <span style={{ color: 'var(--text-2)', fontSize: 12 }}>{tr('pages.endpointAgents.installOnLaptopsThenEnrollWith')}</span>
        </div>
        <div style={{ display: 'flex', gap: 10, padding: 14, flexWrap: 'wrap' }}>
          {getOS_TARGETS(t).map((t) => (
            <button
              key={t.key}
              className="ent-btn"
              disabled={busy === `dl-${t.key}`}
              onClick={() => download(t.key, t.label)}
            >
              <Icon name={t.icon as any} className={busy === `dl-${t.key}` ? 'spin' : ''} /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <AgentControlPanel apiUrl={apiUrl} />

      {/* Browser extension packaging + install */}
      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">
          <span><Icon name="download" /> {tr('pages.endpointAgents.browserExtensionChromeEdge')}</span>
          <span style={{ color: 'var(--text-2)', fontSize: 12 }}>
            {tr('pages.endpointAgents.blocksPIISecretPastesIntoWeb')}
          </span>
        </div>
        <div style={{ padding: 14 }}>
          <button className="ent-btn primary" disabled={busy === 'dl-ext'} onClick={downloadExtension}>
            <Icon name="download" className={busy === 'dl-ext' ? 'spin' : ''} /> {tr('pages.endpointAgents.downloadExtensionZip')}
          </button>
          <div style={{ marginTop: 8, color: 'var(--text-2)', fontSize: 12 }}>
            {tr('pages.endpointAgents.bakedAgenti5Server')} <code>{deriveExtensionServer()}</code>
          </div>
          <ol style={{ margin: '12px 0 0', paddingLeft: 18, color: 'var(--text-2)', fontSize: 13, lineHeight: 1.7 }}>
            <li>{tr('pages.endpointAgents.unzipTheDownloadedFileToA')}</li>
            <li>{tr('pages.endpointAgents.open')} <code>chrome://extensions</code> {tr('pages.endpointAgents.or')} <code>edge://extensions</code>{tr('pages.endpointAgents.andEnable')} <b>{tr('pages.endpointAgents.developerMode')}</b>.</li>
            <li>{tr('pages.endpointAgents.click')} <b>{tr('pages.endpointAgents.loadUnpacked')}</b> {tr('pages.endpointAgents.andSelectTheUnzippedFolder')}</li>
            <li>{tr('pages.endpointAgents.doneItAutoEnrollsAndAppears')} <b>{tr('pages.endpointAgents.browserExt')}</b> {tr('pages.endpointAgents.device')}</li>
          </ol>
        </div>
      </div>

      {/* Fleet table */}
      <div className="ent-card">
        <div className="ent-table-wrap scroll">
          <table className="ent-table">
            <thead>
              <tr>
                <th>{tr('pages.endpointAgents.device2')}</th>
                <th style={{ width: 130 }}>{tr('pages.endpointAgents.layer')}</th>
                <th style={{ width: 180 }}>{tr('pages.endpointAgents.ipAddressEs')}</th>
                <th style={{ width: 80 }}>{tr('pages.endpointAgents.os')}</th>
                <th style={{ width: 80 }}>{tr('pages.endpointAgents.status')}</th>
                <th style={{ width: 150 }}>{tr('pages.endpointAgents.agent')}</th>
                <th style={{ width: 100 }}>{tr('pages.endpointAgents.enforcement')}</th>
                <th style={{ width: 150 }}>{tr('pages.endpointAgents.lastSeen')}</th>
                <th style={{ width: 150 }}>{tr('pages.endpointAgents.enrolled')}</th>
                <th style={{ width: 170, textAlign: 'right' }}>{tr('pages.endpointAgents.action')}</th>
              </tr>
            </thead>
            <tbody>
              {devices.length === 0 && (
                <tr>
                  <td colSpan={10} className="ent-empty">
                    {tr('pages.endpointAgents.noEndpointAgentsEnrolledYetClick')}
                  </td>
                </tr>
              )}
              {devices.map((d) => {
                const ips = parseIps(d.ips);
                const kind = d.kind || 'agent';
                const health = d.health || 'no_heartbeat';
                const detail = d.health_detail || null;
                const isOpen = expanded === d.device_id;
                const healthAt = parseServerTime(d.health_at);
                const heartbeatAgo = healthAt !== null ? formatRelative(healthAt, i18n.language) : tr('pages.endpointAgents.never');
                const issues = detail?.issues || [];
                const hoverTitle = [
                  `${tr('pages.endpointAgents.heartbeat')}: ${heartbeatAgo}`,
                  ...(health === 'no_heartbeat' ? [tr('pages.endpointAgents.healthNoHeartbeatHint')] : []),
                  ...issues.map((i) => `• ${i.detail || i.key}`),
                ].join('\n');
                return (
                <React.Fragment key={d.device_id}>
                <tr>
                  <td>
                    <strong>{d.hostname || d.device_id}</strong>
                    {d.owner_user && <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>{d.owner_user}</div>}
                    <div style={{ fontSize: 10, color: 'var(--text-2)', marginTop: 2, fontFamily: 'monospace' }}>{d.device_id}</div>
                  </td>
                  <td>
                    <span className={`badge ${LAYER_BADGE[kind] || 'badge-info'}`}>{LAYER_LABEL[kind] || kind}</span>
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>
                    {ips.length ? ips.join(', ') : <span style={{ color: 'var(--text-2)' }}>—</span>}
                  </td>
                  <td>{d.os || '—'}</td>
                  <td>
                    <span className={`badge ${d.status === 'active' ? 'badge-success' : 'badge-error'}`}>{d.status}</span>
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => setExpanded(isOpen ? null : d.device_id)}
                      title={hoverTitle}
                      aria-expanded={isOpen}
                      aria-label={tr('pages.endpointAgents.showAgentHealth')}
                      style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'start', color: 'inherit', font: 'inherit' }}
                    >
                      <span className={`badge ${HEALTH_BADGE[health] || 'badge-unknown'}`}>{tr(HEALTH_LABEL_KEY[health] || HEALTH_LABEL_KEY.no_heartbeat)}</span>
                      {health === 'degraded' && issues.length > 0 && (
                        <span style={{ marginInlineStart: 4, fontSize: 11, color: 'var(--text-2)' }}>({issues.length})</span>
                      )}
                      <div style={{ fontSize: 10, color: 'var(--text-2)', marginTop: 2, fontFamily: 'monospace' }}>
                        {d.agent_version || '—'}
                      </div>
                    </button>
                  </td>
                  <td>
                    {d.enforcement === 'block' ? (
                      <span className="badge badge-error">{tr('pages.endpointAgents.blocked')}</span>
                    ) : d.enforcement === 'allow' ? (
                      <span className="badge badge-success">{tr('pages.endpointAgents.allowed')}</span>
                    ) : (
                      <span style={{ color: 'var(--text-2)' }}>—</span>
                    )}
                  </td>
                  <td>{d.last_seen ? parseServerDate(d.last_seen).toLocaleString() : '—'}</td>
                  <td>{d.enrolled_at ? parseServerDate(d.enrolled_at).toLocaleString() : '—'}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {d.status === 'active' && (
                      <>
                        {d.enforcement === 'block' ? (
                          <button className="ent-btn sm" disabled={busy === d.device_id} onClick={() => setEnforcement(d.device_id, 'allow')} title={tr('pages.endpointAgents.unblockThisEndpoint')}>
                            <Icon name="checkBadge" /> {tr('pages.endpointAgents.unblock')}
                          </button>
                        ) : (
                          <button className="ent-btn sm" disabled={busy === d.device_id} onClick={() => setEnforcement(d.device_id, 'block')} title={tr('pages.endpointAgents.quarantineThisEndpointFromAllAI')}>
                            <Icon name="shield" /> {tr('pages.endpointAgents.blockAllAI')}
                          </button>
                        )}
                        <button
                          className="ent-btn sm"
                          style={{ marginLeft: 6 }}
                          onClick={() => navigate(`/llm-access-policy?device=${encodeURIComponent(d.device_id)}`)}
                          title={tr('pages.endpointAgents.chooseWhichAIServicesThisEndpoint')}
                        >
                          <Icon name="shield" /> {tr('pages.endpointAgents.llmAccess')}
                        </button>
                        <button className="ent-btn sm" disabled={busy === d.device_id} onClick={() => revoke(d.device_id)} style={{ marginLeft: 6 }}>
                          <Icon name="deny" /> {tr('pages.endpointAgents.revoke')}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
                {isOpen && (
                  <tr className="audit-detail-row">
                    <td colSpan={10}>
                      <AgentHealthPanel detail={detail} heartbeatAgo={heartbeatAgo} health={health} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showEnroll && (
        <Modal
          title={tr('pages.endpointAgents.enrollAnEndpointDevice')}
          icon="key"
          onClose={() => setShowEnroll(false)}
          footer={<button className="ent-btn" onClick={() => setShowEnroll(false)}>{tr('pages.endpointAgents.close')}</button>}
        >
          <p className="subtitle">
            {tr('pages.endpointAgents.configureTheAgentWithThisShort')}
          </p>
          {generating && <div className="ent-empty">{tr('pages.endpointAgents.generatingToken')}</div>}
          {enrollToken && (
            <>
              <div className="ent-field">
                <label>{tr('pages.endpointAgents.enrollmentToken')} {enrollExpiry ? tr('pages.endpointAgents.enrollmentExpires', { date: parseServerDate(enrollExpiry).toLocaleString() }) : ''}</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="ent-input" readOnly value={enrollToken} style={{ fontFamily: 'monospace', fontSize: 11 }} />
                  <button className="ent-btn" onClick={() => copy(enrollToken)}><Icon name="copy" /></button>
                </div>
              </div>
              <div className="ent-field">
                <label>{tr('pages.endpointAgents.linuxQuickStart')}</label>
                <textarea
                  className="ent-input"
                  readOnly
                  rows={4}
                  style={{ fontFamily: 'monospace', fontSize: 11 }}
                  value={`sudo ztai-bdr-agent enroll \\\n  --server ${apiUrl} \\\n  --token ${enrollToken}\nsudo systemctl enable --now ztai-bdr-agent`}
                />
              </div>
              <div className="ent-field">
                <label>{tr('pages.endpointAgents.mobileQuickStart')}</label>
                <p className="subtitle" style={{ margin: '0 0 6px' }}>{tr('pages.endpointAgents.mobileQuickStartHint')}</p>
                <textarea
                  className="ent-input"
                  readOnly
                  rows={6}
                  style={{ fontFamily: 'monospace', fontSize: 11 }}
                  value={`server_url        = ${apiUrl}\nenrollment_token = ${enrollToken}\nuser_id           = {{userprincipalname}}   (Intune; Jamf: $EMAIL; Workspace ONE: {EmailAddress})\nscoped_packages   = (optional) comma-separated package names, or *`}
                />
              </div>
              <div className="ent-field">
                <label>{tr('pages.endpointAgents.macosQuickStart')}</label>
                <textarea
                  className="ent-input"
                  readOnly
                  rows={4}
                  style={{ fontFamily: 'monospace', fontSize: 11 }}
                  value={`sudo mkdir -p /etc/ztai\nprintf 'server_url = "%s"\\nenrollment_token = "%s"\\n' '${apiUrl}' '${enrollToken}' | sudo tee /etc/ztai/bdr-agent.toml >/dev/null\nsudo /usr/local/share/ztai/setup.sh /etc/ztai/bdr-agent.toml`}
                />
              </div>
            </>
          )}
        </Modal>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 1000, maxWidth: 380,
          padding: '12px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500, lineHeight: 1.45,
          background: 'var(--bg-2)', color: 'var(--text)', border: '1px solid var(--border-strong)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.18), 0 1px 4px rgba(0,0,0,0.1)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ flexShrink: 0, width: 8, height: 8, borderRadius: '50%', background: 'var(--success)', display: 'inline-block' }} />
          {toast}
        </div>
      )}
    </div>
  );
};

export default EndpointAgents;
